'use client';

import React, { useRef, useEffect } from 'react';
import { Box, Text, VStack, HStack, Accordion, Input } from '@skin-seoul/design-system';
import CustomCheckbox from '@/components/ui/CustomCheckbox';
import CustomAccordionIndicator from '@/components/ui/CustomAccordionIndicator';
import { CLASSNAME } from '@skin-seoul/service';
const ACCORDION_SCROLL_DELAY_MS = 100;
interface FilterItem {
  termId: number;
  name: string;
  count: number;
}
interface FilterSectionProps {
  value: string;
  title: string;
  items?: FilterItem[];
  selectedItems: number[];
  onItemChange: (termId: number, checked: boolean) => void;
  showSearch?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  maxHeight?: string;
  skeletonCount?: number;
  accordionValue?: string[];
  isLast?: boolean;
  autoScrollOnExpand?: boolean;
  isLoading?: boolean;
}
const LoadingSkeleton = ({
  count = 6
}: {
  count?: number;
}) => <VStack align="stretch" gap={2} data-sentry-element="VStack" data-sentry-component="LoadingSkeleton" data-sentry-source-file="FilterSection.tsx">
    {Array.from({
    length: count
  }).map((_, index) => <HStack key={index} gap={2}>
        <Box width="16px" height="16px" backgroundColor="neutral.100" borderRadius="2px" />
        <Box width="75%" height="16px" backgroundColor="neutral.100" borderRadius="4px" />
      </HStack>)}
  </VStack>;
const FilterSection = ({
  value,
  title,
  items,
  selectedItems,
  onItemChange,
  showSearch = false,
  searchQuery = '',
  onSearchChange,
  searchPlaceholder = 'Search here',
  maxHeight = '200px',
  skeletonCount = 6,
  accordionValue,
  isLast = false,
  autoScrollOnExpand = true,
  isLoading = false
}: FilterSectionProps) => {
  const hasData = Boolean(items?.length && items.length > 0);
  const isSearchActive = showSearch && searchQuery.trim() !== '';
  const hasNoResults = items?.length === 0 && isSearchActive;
  const hasNoData = items?.length === 0 && !isSearchActive;
  const accordionItemRef = useRef<HTMLDivElement>(null);
  const isExpanded = accordionValue?.includes(value);
  const prevExpandedRef = useRef(isExpanded);

  // Auto-scroll into view when accordion is expanded (mobile UX improvement)
  // This ensures users can see the expanded content without manual scrolling
  useEffect(() => {
    const wasCollapsed = !prevExpandedRef.current;
    const isNowExpanded = isExpanded;
    prevExpandedRef.current = isExpanded;
    if (autoScrollOnExpand && wasCollapsed && isNowExpanded && accordionItemRef.current) {
      const timeoutId = setTimeout(() => {
        accordionItemRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }, ACCORDION_SCROLL_DELAY_MS);
      return () => clearTimeout(timeoutId);
    }
  }, [isExpanded, autoScrollOnExpand]);
  return <Accordion.Item ref={accordionItemRef} value={value} borderBottom={isLast ? 'none' : '1px solid'} borderColor={isLast ? 'transparent' : 'neutral.100'} data-sentry-element="Accordion.Item" data-sentry-component="FilterSection" data-sentry-source-file="FilterSection.tsx">
      <Accordion.ItemTrigger justifyContent="space-between" cursor="pointer" paddingY={6} data-sentry-element="Accordion.ItemTrigger" data-sentry-source-file="FilterSection.tsx">
        <Text fontWeight="regular" flex="1" textAlign="left" textStyle="paragraph.medium" color="neutral.600" className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Text" data-sentry-source-file="FilterSection.tsx">
          {title}
        </Text>
        <CustomAccordionIndicator value={value} accordionValue={accordionValue} data-sentry-element="CustomAccordionIndicator" data-sentry-source-file="FilterSection.tsx" />
      </Accordion.ItemTrigger>
      <Accordion.ItemContent paddingBottom={4} data-sentry-element="Accordion.ItemContent" data-sentry-source-file="FilterSection.tsx">
        <VStack align="stretch" gap={3} data-sentry-element="VStack" data-sentry-source-file="FilterSection.tsx">
          {showSearch && onSearchChange && <Input placeholder={searchPlaceholder} value={searchQuery} onChange={e => onSearchChange(e.target.value)} size="sm" variant="outline" borderColor="neutral.100" _focus={{
          outline: 'none',
          boxShadow: 'none'
        }} />}

          {hasData && items && <VStack align="stretch" gap={2} maxHeight={maxHeight} overflowY="auto">
              {items.map(item => <CustomCheckbox key={item.termId} checked={selectedItems.includes(item.termId)} label={`${item.name} (${item.count})`} onChange={checked => onItemChange(item.termId, checked)} />)}
            </VStack>}

          {hasNoResults && <Text textStyle="paragraph.small" color="neutral.600" textAlign="center" paddingY={3}>
              No matching brand found for your search.
            </Text>}

          {hasNoData && <Text textStyle="paragraph.small" color="neutral.600" textAlign="center" paddingY={3}>
              No data available.
            </Text>}

          {isLoading && <>
              {showSearch && <Box height="32px" backgroundColor="neutral.100" borderRadius="4px" />}
              <LoadingSkeleton count={skeletonCount} />
            </>}
        </VStack>
      </Accordion.ItemContent>
    </Accordion.Item>;
};
export default FilterSection;